import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const translations = {
  en: {
    title: "Personal Reflection",
    subtitle: "Share your thoughts and experiences to help us understand your values, achievements, and approach to challenges.",
    ambition: "What is your ambition (personal or work)?",
    ambitionPlaceholder: "Share your personal or work-related ambitions...",
    ambitionStrategy: "How do you ensure that your ambition becomes a reality?",
    ambitionStrategyPlaceholder: "Describe the steps and strategies you take to achieve your ambitions...",
    greatestSuccess: "What is your greatest success to date and what did you do to achieve it?",
    greatestSuccessPlaceholder: "Share your most significant achievement and the actions that led to it...",
    fitnessRituals: "What are currently your most important rituals for staying or becoming physically and mentally fit?",
    fitnessRitualsPlaceholder: "Describe your daily or weekly routines for maintaining physical and mental wellbeing...",
    back: "Back",
    startAnalysis: "Start Analysis",
    summaryTitle: "Personal Reflection Insights",
    summaryAmbition: "Ambition",
    summaryStrategy: "Strategy for Ambition",
    summarySuccess: "Greatest Success",
    summaryFitness: "Fitness & Mental Wellbeing"
  },
  nl: {
    title: "Persoonlijke Reflectie",
    subtitle: "Deel uw gedachten en ervaringen om ons te helpen uw waarden, prestaties en aanpak van uitdagingen te begrijpen.",
    ambition: "Wat is uw ambitie (persoonlijk of werk)?",
    ambitionPlaceholder: "Deel uw persoonlijke of werkgerelateerde ambities...",
    ambitionStrategy: "Hoe zorgt u ervoor dat uw ambitie werkelijkheid wordt?",
    ambitionStrategyPlaceholder: "Beschrijf de stappen en strategieën die u neemt om uw ambities te bereiken...",
    greatestSuccess: "Wat is uw grootste succes tot nu toe en wat heeft u gedaan om het te bereiken?",
    greatestSuccessPlaceholder: "Deel uw belangrijkste prestatie en de acties die ertoe hebben geleid...",
    fitnessRituals: "Wat zijn momenteel uw belangrijkste rituelen om fysiek en mentaal fit te blijven of te worden?",
    fitnessRitualsPlaceholder: "Beschrijf uw dagelijkse of wekelijkse routines voor het behouden van fysiek en mentaal welzijn...",
    back: "Terug",
    startAnalysis: "Start Analyse",
    summaryTitle: "Inzichten Persoonlijke Reflectie",
    summaryAmbition: "Ambitie",
    summaryStrategy: "Strategie voor Ambitie",
    summarySuccess: "Grootste Succes",
    summaryFitness: "Fitness & Mentale Gezondheid"
  }
};

export default function ReflectionStep({ onNext, setActiveIndicator, reflectionSummary, setReflectionSummary, isReflectionFormValid, setIsReflectionFormValid, reflectionAnswers, setReflectionAnswers, isDutch, setReflection }) {
  const navigate = useNavigate();
  const t = translations[isDutch ? 'nl' : 'en'];

  const handleInputChange = (field, value) => {
    setReflectionAnswers(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNext = () => {
    onNext();
  };

  useEffect(() => {
    const summary = `${t.summaryTitle}: 
    ${reflectionAnswers.ambition ? `${t.summaryAmbition}: ${reflectionAnswers.ambition}` : ''}
    ${reflectionAnswers.ambitionStrategy ? `${t.summaryStrategy}: ${reflectionAnswers.ambitionStrategy}` : ''}
    ${reflectionAnswers.greatestSuccess ? `${t.summarySuccess}: ${reflectionAnswers.greatestSuccess}` : ''}
    ${reflectionAnswers.fitnessRituals ? `${t.summaryFitness}: ${reflectionAnswers.fitnessRituals}` : ''}`;

    setReflectionSummary(summary);
  }, [reflectionAnswers, t]);

  useEffect(() => {
    const isValid = Object.values(reflectionAnswers).every(answer => answer.trim() !== "");
    setIsReflectionFormValid(isValid);
  }, [reflectionAnswers]);

  useEffect(() => {
    setReflection({
      ambition: reflectionAnswers.ambition,
      ambitionStrategy: reflectionAnswers.ambitionStrategy,
      greatestSuccess: reflectionAnswers.greatestSuccess,
      fitnessRituals: reflectionAnswers.fitnessRituals
    });
  }, [reflectionAnswers]);

  const HandleBack = () => {
    setActiveIndicator(3);
  };

  const handleSubmit = async () => {
    navigate("/analysis");
  };

  return (
    <div>
      <div className='mb-4'>
        <h1 className="text-5xl font-bold mb-4 text-primary">{t.title}</h1>
        <p>{t.subtitle}</p>
      </div>

      <div className="border rounded-md p-6 space-y-6">
        <div>
          <label className="text-left mb-2 text-xl font-bold block">{t.ambition}</label>
          <textarea
            value={reflectionAnswers.ambition}
            onChange={(e) => handleInputChange('ambition', e.target.value)}
            className="w-full p-2 border rounded-md min-h-[100px]"
            placeholder={t.ambitionPlaceholder}
          />
        </div>

        <div>
          <label className="text-left mb-2 text-xl font-bold block">{t.ambitionStrategy}</label>
          <textarea
            value={reflectionAnswers.ambitionStrategy}
            onChange={(e) => handleInputChange('ambitionStrategy', e.target.value)}
            className="w-full p-2 border rounded-md min-h-[100px]"
            placeholder={t.ambitionStrategyPlaceholder}
          />
        </div>

        <div>
          <label className="text-left mb-2 text-xl font-bold block">{t.greatestSuccess}</label>
          <textarea
            value={reflectionAnswers.greatestSuccess}
            onChange={(e) => handleInputChange('greatestSuccess', e.target.value)}
            className="w-full p-2 border rounded-md min-h-[100px]"
            placeholder={t.greatestSuccessPlaceholder}
          />
        </div>

        <div>
          <label className="text-left mb-2 text-xl font-bold block">{t.fitnessRituals}</label>
          <textarea
            value={reflectionAnswers.fitnessRituals}
            onChange={(e) => handleInputChange('fitnessRituals', e.target.value)}
            className="w-full p-2 border rounded-md min-h-[100px]"
            placeholder={t.fitnessRitualsPlaceholder}
          />
        </div>
      </div>

      <div className="flex gap-2 mt-4 justify-end">
        <button
          className="py-2 px-4 rounded-md border"
          onClick={HandleBack}
        >
          {t.back}
        </button>
        <button
          className={`py-2 px-4 rounded-md bg-buttonColor text-white border ${!isReflectionFormValid ? 'opacity-50 cursor-not-allowed' : ''}`}
          onClick={handleSubmit}
          disabled={!isReflectionFormValid}
        >
          {t.startAnalysis}
        </button>
      </div>
    </div>
  );
} 